import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db, auth } from "./config/firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import "./Login.css";
import LoadingScreen from "./components/LoadingScreen";
import { Alert, Card } from "react-bootstrap";
import { Course, UserData } from "./types/firebase";
import { signInWithEmailAndPassword, signInAnonymously } from "firebase/auth";
import { doc, getDoc, collection, onSnapshot } from "firebase/firestore";
import { SESSION_GROUP_ONLY_ENTRY } from "./constants/entryFlow";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [user, loading] = useAuthState(auth as any);
  const navigate = useNavigate();
  const groupID = localStorage.getItem('group');
  const courseID = localStorage.getItem('course');
  const [group, setGroup] = useState<UserData | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [coursesData, setCoursesData] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const groupOnlyEntry =
    typeof sessionStorage !== "undefined" &&
    sessionStorage.getItem(SESSION_GROUP_ONLY_ENTRY) === "1";

  useEffect(() => {
    if (!groupID) {
      setGroup(null);
      setIsLoading(false);
      return;
    }

    const fetchGroupData = async () => {
      setIsLoading(true);
      try {
        // Firestore rules often allow group/course reads only for signed-in users (incl. anonymous).
        // Group deep links must match GroupChecker behavior or getDoc fails and group never loads.
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }
        const groupRef = doc(db, 'groups', groupID);
        const docSnap = await getDoc(groupRef);
        if (docSnap.exists()) {
          setGroup(docSnap.data() as UserData);
        } else {
          setErrorMessage('Group not found. Please check your group link and try again.');
        }
      } catch (error) {
        console.error('Error fetching group:', error);
        setErrorMessage('Failed to load group data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGroupData();
  }, [groupID]);

  useEffect(() => {
    if (groupID && courseID) {
      const fetchCourseData = async () => {
        try {
          if (!auth.currentUser) {
            await signInAnonymously(auth);
          }
          const courseRef = doc(db, 'groups', groupID, 'courses', courseID);
          const docSnap = await getDoc(courseRef);
          if (docSnap.exists()) {
            setCourse(docSnap.data() as Course);
          } else {
            setErrorMessage('Course not found. Please check your course link and try again.');
          }
        } catch (error) {
          console.error('Error fetching course:', error);
          setErrorMessage('Failed to load course data. Please try again.');
        }
      };
      fetchCourseData();
    } else {
      setCourse(null);
    }
  }, [groupID, courseID]);

  useEffect(() => {
    if (loading) return;
    if (user && !user.isAnonymous) {
      navigate("/page/teetimes", { replace: true });
    }
  }, [user, loading, navigate]);

  const refreshCourses = useCallback(async () => {
    if (!group || !groupID) {
      return () => { }; // Return empty cleanup function if no group/groupID
    }

    try {
      const coursesRef = collection(db, 'groups', groupID, 'courses');
      const unsubscribe = onSnapshot(coursesRef,
        (snapshot) => {
          const mappedData = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Course));
          setCoursesData(mappedData);
        },
        (error) => {
          console.error('Error in courses snapshot:', error);
          setErrorMessage('Failed to load courses. Please try again.');
        }
      );
      return unsubscribe;
    } catch (err) {
      console.error('Error fetching courses:', err);
      setErrorMessage('Failed to load courses. Please try again.');
      return () => { }; // Return empty cleanup function on error
    }
  }, [group, groupID]);

  useEffect(() => {
    if (groupOnlyEntry) {
      return;
    }
    if (!group || !groupID) {
      return;
    }

    let unsubscribe: (() => void) | undefined;

    refreshCourses().then(unsub => {
      unsubscribe = unsub;
    }).catch(err => {
      console.error('Error setting up courses subscription:', err);
      setErrorMessage('Failed to load courses. Please try again.');
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [refreshCourses, group, groupID, groupOnlyEntry]);

  const handleSetCurrentCourse = async (id: string) => {
    if (!groupID) {
      setErrorMessage('No group selected. Please try again.');
      return;
    }
    try {
      localStorage.setItem('course', id);
      const courseRef = doc(db, 'groups', groupID, 'courses', id);
      const docSnap = await getDoc(courseRef);
      if (docSnap.exists()) {
        setCourse(docSnap.data() as Course);
        navigate("/", { replace: true });
      } else {
        setErrorMessage('Course not found. Please try again.');
      }
    } catch (error) {
      console.error('Error setting current course:', error);
      setErrorMessage('Failed to set current course. Please try again.');
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.code === 'auth/invalid-email') {
        setErrorMessage('The email address is invalid. Please check it and try again.');
      } else if (err.code === 'auth/user-not-found') {
        setErrorMessage('No user was found with that email address. Click the "Register" link below to create an account.');
      } else if (err.code === 'auth/wrong-password') {
        setErrorMessage('Wrong password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setErrorMessage('Your account is temporarily suspended because of too many incorrect login attempts. You can reset your password to fix this immediately.');
      } else {
        setErrorMessage('Oops! Something went wrong. Please check your credentials and try again.');
      }
    }
  };

  if (loading || isLoading) {
    return <LoadingScreen />;
  }

  if (!groupID) {
    return (
      <div className="login">
        <div className="login__container">
          <Alert variant="warning">
            Please select a group first.
          </Alert>
        </div>
      </div>
    );
  }

  const groupDisplayName =
    (group as any)?.name ||
    (group as any)?.groupName ||
    (group as any)?.title ||
    "your organization";

  const forgotPasswordTo = courseID
    ? `/page/reset?group=${encodeURIComponent(groupID)}&course=${encodeURIComponent(courseID)}`
    : `/page/reset?group=${encodeURIComponent(groupID)}`;

  const loginForm = (
    <>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      <form onSubmit={handleSignIn}>
        <input
          type="email"
          className="form-control d-block w-100 mb-3 p-2"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="E-mail Address"
          required
        />
        <input
          type="password"
          className="form-control d-block w-100 mb-3 p-2"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          required
        />
        <button
          type="submit"
          className="btn btn-primary w-100 btn-block mb-3"
        >
          Login
        </button>
      </form>
      <small className="text-muted text-sm-center d-block mb-2">
        <Link to={forgotPasswordTo}>Forgot Password</Link>
      </small>
      <small className="text-muted text-sm-center d-block">
        Don&apos;t have an account? <Link to="/page/register">Register</Link> now.
      </small>
    </>
  );

  return (
    <div className="login">
      {groupID && !group && !course && (
        <div className="login__container">
          <Alert variant="danger">
            {errorMessage ??
              "We could not load this group. Check the link, or open the full reservation link from your course website."}
          </Alert>
        </div>
      )}

      {groupOnlyEntry && group && (
        <div className="login__container">
          <p>
            Please login to reserve a tee time for
            <br />
            <strong>{groupDisplayName}</strong>.
          </p>
          {loginForm}
        </div>
      )}

      {!groupOnlyEntry && group && !course && (
        <div className="login__container">
          <p>Please choose which course you would like to play at.</p>

          {coursesData.length > 0 && coursesData.map((courseData) => (
            <Card
              onClick={() => handleSetCurrentCourse(courseData.id)}
              className="mb-3 courseCardLink"
              key={courseData.id}
            >
              <Card.Body>
                <Card.Title className="text-center">
                  {courseData.name}
                </Card.Title>
                <Card.Text>
                  {courseData.city && `${courseData.city}, `}
                  {courseData.state && courseData.state}
                  {courseData.province && courseData.province}
                </Card.Text>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {!groupOnlyEntry && group && course && (
        <div className="login__container">
          <p>
            Please login to reserve a tee time for
            <br />
            <strong>{course.name}</strong>.
          </p>
          {loginForm}
        </div>
      )}
    </div>
  );
}

export default Login;
